import './MatchEditLigue.scss';

import { Hidden } from '@mui/material';
import { useWindowSize } from '@uidotdev/usehooks';
import { EventStatusEnum } from 'domain/event/EventStatusEnum';
import { MatchGroup } from 'domain/event/match/MatchGroup';
import { toBracketTournamentModel, UnpyMatchFilters } from 'domain/event/match/UnpyMatch';
import { UnpyEvent } from 'domain/event/UnpyEvent';
import { EventMatchsParametersProps } from 'primary/classements/MatchsParametersByEventType';
import { LoadingScreen } from 'primary/Components/LoadingScreen/LoadingScreen';
import { MatchListByGroupsMobile } from 'primary/events/components/matchs/edit/mobile/MatchListByGroupsMobile';
import { HeaderMatchsEdit } from 'primary/events/components/matchs/HeaderMatchsEdit';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import React, { useEffect } from 'react';

import { MatchListViewByGroup } from './MatchEditLigue';

const Bracket = React.lazy(() =>
  import('primary/events/components/matchs/view/Bracket').then((module) => ({
    default: module.Bracket,
  })),
);

const isSafari =
  /constructor/i.test(window?.HTMLElement?.toString()) ||
  (function (p) {
    return p.toString() === '[object SafariRemoteNotification]';
    //@ts-ignore
  })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

export const MatchEditTournament = ({
  event,
  retry,
  asView,
}: EventMatchsParametersProps) => {
  const [mode, setMode] = React.useState<'bracket' | 'session'>(
    isSafari ? 'session' : 'bracket',
  );
  const { authRepository } = useContextDependency();
  const [filters, setFilters] = React.useState<UnpyMatchFilters>({
    onlyScoreUnset: undefined,
    textValue: undefined,
  });

  const setFilterByKey = (
    key: keyof UnpyMatchFilters,
    val?: string | number | boolean,
  ) => {
    setFilters({ ...filters, [key]: val });
  };

  return (
    <div className={'matchEditLigue'}>
      <Hidden smUp>
        <HeaderMatchsEdit
          withRoundFilter={false}
          retry={retry}
          event={event}
          filters={filters}
          setFilters={setFilterByKey}
          withOnlyMyMatchs={
            asView && event.creator?.id !== authRepository?.currentUser?.id
          }
          withOnlyScoreUnset={!asView}
        />
        <MatchListByGroupsMobile eventId={event.id} filters={filters} asView={asView} />
      </Hidden>

      <Hidden smDown>
        <HeaderMatchsEdit
          retry={retry}
          event={event}
          onChangeMode={setMode}
          mode={mode}
          withRoundFilter={false}
          filters={filters}
          withOnlyMyMatchs={
            asView && event.creator?.id !== authRepository?.currentUser?.id
          }
          withOnlyScoreUnset={!asView}
          hideSwitchMode={isSafari}
          withTextField
          setFilters={setFilterByKey}
        />
        {mode === 'session' && (
          <MatchListViewByGroup filters={filters} event={event} asView={asView} />
        )}
        {mode === 'bracket' && (
          <MatchsEditByBracket filters={filters} event={event} asView={asView} />
        )}
      </Hidden>
    </div>
  );
};

type MatchsEditByBracketProps = {
  event: UnpyEvent;
  filters: UnpyMatchFilters;
  asView?: boolean;
};

export const MatchsEditByBracket = ({
  event,
  filters,
  asView,
}: MatchsEditByBracketProps) => {
  const { matchsRepository } = useContextDependency();
  const { width } = useWindowSize();
  const finalWidth = Math.max(width ?? 0 - 50, 500);
  const {
    setFilters,
    activeFilters,
    fetchedResource,
    nextPage,
    loading,
    retry,
    addToCurrentPage,
  } = useFetchWithFilterPage<MatchGroup, UnpyMatchFilters>({
    keyStoreFilters: 'matchs-ligue-filters',
    staticFilters: {
      idEvent: event.id,
      withMatchs: true,
    },
    filters: {
      roundFrom: 0,
      roundTo: Math.ceil(Math.log(event.totalParticipants ?? 0) / Math.log(2)),
      ...filters,
    },
    callApi: (filters, page) => matchsRepository.searchGroup(filters, page, -1),
  });

  // eslint-disable-next-line prefer-spread
  const maxGroupPhaseOrder = Math.max.apply(
    Math,
    fetchedResource?.content?.map((group) => group.phaseOrder) ?? [],
  );

  useEffect(() => {
    setFilters({ ...(activeFilters?.filters ?? {}), ...filters });
  }, [filters]);
  const formattedMatchs = fetchedResource?.content
    ?.map((group) => ({
      ...group,
      matchs: group.matchs.map((match) =>
        toBracketTournamentModel(match, maxGroupPhaseOrder),
      ),
    }))
    .flatMap((group) => group.matchs);

  return (
    <div className={'-bracketTournament'}>
      {loading && <LoadingScreen loading />}
      {formattedMatchs && (
        <React.Suspense fallback={<LoadingScreen loading />}>
          <Bracket
            isEditable={event?.status === EventStatusEnum.ON_GOING && !asView}
            event={event}
            formattedMatchs={formattedMatchs}
            retry={retry}
          />
        </React.Suspense>
      )}
    </div>
  );
};
